import { Clock, MousePointerClick, SlidersHorizontal } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { IconBtn } from "../../ui";
import { Pop, useClosePops } from "../../shell/Pop";
import {
  CALL_SOURCES,
  DEFAULT_CALL_FILTERS,
  DURATION_STEPS,
  callFiltersActive,
  type CallFilters,
} from "./filters";

/**
 * Where the number came from, and how long the call ran.
 *
 * Both are server-side (`source`, `minSec` on the list route). The pane loads a
 * hundred rows at a time, and a filter that only sifted those hundred would
 * answer "no calls from the scanner" when it meant "none in the newest hundred".
 */
export function CallFilterMenu({
  filters,
  onFilters,
}: {
  filters: CallFilters;
  onFilters: (next: CallFilters) => void;
}) {
  const t = useT();
  const close = useClosePops();
  const active = callFiltersActive(filters);
  const set = (patch: Partial<CallFilters>) => onFilters({ ...filters, ...patch });

  return (
    <Pop
      trigger={(open) => (
        <IconBtn
          className={cn(active && "crm-cb-dot", open && "is-on")}
          title={t("calls.filter.title")}
          aria-label={t("calls.filter.title")}
          aria-expanded={open}
        >
          <SlidersHorizontal className="size-4" />
        </IconBtn>
      )}
    >
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("calls.filter.title")}</div>
        <div className="crm-menu-s">{t("calls.filter.sub")}</div>
      </div>

      <div className="crm-msect">{t("calls.filter.source")}</div>
      <button
        type="button"
        className={cn("crm-mi", filters.source === "all" && "is-on")}
        onClick={() => set({ source: "all" })}
      >
        <MousePointerClick className="size-4" />
        {t("calls.filter.allSources")}
      </button>
      {CALL_SOURCES.map((source) => (
        <button
          key={source}
          type="button"
          className={cn("crm-mi", filters.source === source && "is-on")}
          onClick={() => set({ source })}
        >
          <MousePointerClick className="size-4" />
          {t(`calls.source.${source}`)}
        </button>
      ))}

      <div className="crm-menu-sep" />
      <div className="crm-msect">{t("calls.filter.duration")}</div>
      {DURATION_STEPS.map((step) => (
        <button
          key={step.key}
          type="button"
          className={cn("crm-mi", filters.minSec === step.sec && "is-on")}
          onClick={() => set({ minSec: step.sec })}
        >
          <Clock className="size-4" />
          {t(`calls.filter.${step.key}`)}
        </button>
      ))}

      {active ? (
        <>
          <div className="crm-menu-sep" />
          <button
            type="button"
            className="crm-mi"
            onClick={() => {
              onFilters(DEFAULT_CALL_FILTERS);
              close();
            }}
          >
            <SlidersHorizontal className="size-4" />
            {t("calls.filter.reset")}
          </button>
        </>
      ) : null}
    </Pop>
  );
}
